import { Reducer } from 'redux';
import {
  MemberLookupActionTypes,
  MemberLookupState,
  INITIAL_MEMBER_LOOKUP_STATE
} from './types';

export const MemberLookupReducer: Reducer<MemberLookupState> = (
  state = INITIAL_MEMBER_LOOKUP_STATE,
  action
) => {
  switch (action.type) {
    case MemberLookupActionTypes.REGISTER_START:
      return {
        ...state,
        loading: true,
        uploadError: null,
        processingPhase:
          action.payload.inputSource === 'MAP_SKETCH'
            ? 'REGISTERING'
            : 'VALIDATING_FILE'
      };

    case MemberLookupActionTypes.REGISTER_SUCCESS: {
      const { lookupId, registrationJobId } = action.payload;
      if (registrationJobId) {
        return {
          ...state,
          loading: false,
          lookupId,
          registrationJobId
        };
      }
      return {
        ...state,
        loading: false,
        lookupId,
        processingPhase: 'AWAITING_PAYMENT'
      };
    }

    case MemberLookupActionTypes.REGISTER_FAILURE: {
      const isMapSketchPhase =
        state.processingPhase === 'REGISTERING' ||
        state.processingPhase === 'AWAITING_PAYMENT';
      return {
        ...state,
        loading: false,
        uploadError: action.payload,
        processingPhase: 'IDLE',
        lookupId:
          isMapSketchPhase && !state.registrationJobId ? null : state.lookupId
      };
    }

    case MemberLookupActionTypes.CLEAR_MAP_SKETCH_REGISTRATION:
      return {
        ...state,
        lookupId: null,
        uploadError: null,
        processingPhase: 'IDLE',
        loading: false
      };

    case MemberLookupActionTypes.UPLOAD_FILE_START:
      return {
        ...state,
        loading: true,
        error: null,
        uploadError: null,
        inputMode: 'FILE_UPLOAD',
        processingPhase: 'UPLOADING_FILE'
      };

    case MemberLookupActionTypes.UPLOAD_FILE_SUCCESS:
      return {
        ...state,
        loading: false,
        fileId: action.payload,
        processingPhase: 'FILE_UPLOAD_READY'
      };

    case MemberLookupActionTypes.UPLOAD_FILE_FAILURE:
      return {
        ...state,
        loading: false,
        uploadError: action.payload,
        processingPhase: 'IDLE'
      };

    case MemberLookupActionTypes.CLEAR_FILE_UPLOAD: {
      const filePhases: Array<typeof state.processingPhase> = [
        'UPLOADING_FILE',
        'FILE_UPLOAD_READY',
        'VALIDATING_FILE',
        'FILE_VALIDATED'
      ];
      if (!filePhases.includes(state.processingPhase)) {
        return state;
      }
      return {
        ...state,
        fileId: null,
        lookupId: null,
        registrationJobId: null,
        uploadError: null,
        shapeCount: null,
        areaKm2: null,
        processingPhase: 'IDLE'
      };
    }

    case MemberLookupActionTypes.POLL_JOB_UPDATE:
      return { ...state, jobStatus: action.payload };

    case MemberLookupActionTypes.POLL_JOB_COMPLETE:
      if (state.processingPhase === 'VALIDATING_FILE') {
        return {
          ...state,
          processingPhase: 'FILE_VALIDATED',
          shapeCount:
            action.payload?.shapeCount != null
              ? action.payload.shapeCount
              : state.shapeCount,
          areaKm2:
            action.payload?.areaKm2 != null
              ? action.payload.areaKm2
              : state.areaKm2
        };
      }
      return state;

    case MemberLookupActionTypes.POLL_JOB_FAILURE:
      if (
        state.processingPhase === 'VALIDATING_FILE' ||
        state.processingPhase === 'FILE_VALIDATED'
      ) {
        return {
          ...state,
          uploadError: action.payload,
          processingPhase: 'IDLE'
        };
      }
      return state;

    case MemberLookupActionTypes.FETCH_LIVE_PRICE_START:
      return {
        ...state,
        livePrice: null,
        livePriceLoading: true,
        livePriceError: null
      };

    case MemberLookupActionTypes.FETCH_LIVE_PRICE_SUCCESS:
      return {
        ...state,
        livePrice: action.payload,
        livePriceLoading: false,
        livePriceError: null
      };

    case MemberLookupActionTypes.FETCH_LIVE_PRICE_FAILURE:
      return {
        ...state,
        livePriceLoading: false,
        livePriceError: action.payload
      };

    case MemberLookupActionTypes.CREATE_CHECKOUT_START:
      return {
        ...state,
        loading: true,
        paymentError: null,
        paymentStatus: 'creating'
      };

    case MemberLookupActionTypes.CREATE_CHECKOUT_SUCCESS: {
      const {
        clientSecret,
        completed,
        transactionReference,
        postPaymentId
      } = action.payload;
      if (clientSecret) {
        return {
          ...state,
          loading: false,
          paymentStatus: 'checkout',
          clientSecret,
          transactionReference: transactionReference ?? null
        };
      }
      if (completed) {
        return {
          ...state,
          loading: false,
          paymentStatus: 'invoice',
          postPaymentId: postPaymentId ?? null,
          clientSecret: null,
          transactionReference: transactionReference ?? null
        };
      }
      return {
        ...state,
        loading: false,
        paymentStatus: 'failed',
        paymentError: 'Unexpected payment response.'
      };
    }

    case MemberLookupActionTypes.CREATE_CHECKOUT_FAILURE:
      return {
        ...state,
        loading: false,
        paymentStatus: 'failed',
        paymentError: action.payload
      };

    case MemberLookupActionTypes.CONFIRM_POSTPAY:
      return { ...state, processingPhase: 'FINDING_MEMBERS' };

    case MemberLookupActionTypes.RESET_PAYMENT_CHECKOUT:
      return {
        ...state,
        paymentStatus: 'idle',
        clientSecret: null,
        checkoutSessionId: null,
        transactionReference: null,
        paymentError: null,
        loading: false,
        processingPhase:
          state.processingPhase === 'PAYMENT_EXPIRED' ||
          state.processingPhase === 'CONFIRMING_PAYMENT'
            ? 'AWAITING_PAYMENT'
            : state.processingPhase
      };

    case MemberLookupActionTypes.POLL_CHECKOUT_STATUS_START:
      return {
        ...state,
        checkoutSessionId: action.payload,
        processingPhase: 'CONFIRMING_PAYMENT'
      };

    case MemberLookupActionTypes.POLL_CHECKOUT_STATUS_COMPLETE: {
      const { paymentIntentId } = action.payload;
      return {
        ...state,
        processingPhase: 'FINDING_MEMBERS',
        transactionReference: paymentIntentId ?? state.transactionReference
      };
    }

    case MemberLookupActionTypes.POLL_CHECKOUT_STATUS_FAILURE:
      return {
        ...state,
        paymentError: action.payload,
        paymentStatus: 'failed',
        clientSecret: null,
        processingPhase: 'PAYMENT_EXPIRED'
      };

    case MemberLookupActionTypes.POLL_STATUS_START:
      return { ...state, processingPhase: 'FINDING_MEMBERS' };

    case MemberLookupActionTypes.POLL_STATUS_UPDATE:
      return { ...state, lookupStatus: action.payload };

    case MemberLookupActionTypes.POLL_STATUS_COMPLETE:
      return { ...state };

    case MemberLookupActionTypes.POLL_STATUS_FAILURE:
      return {
        ...state,
        error: action.payload,
        processingPhase: 'ERROR'
      };

    case MemberLookupActionTypes.FETCH_DOWNLOAD_URL_START:
      return { ...state, loading: true };

    case MemberLookupActionTypes.FETCH_DOWNLOAD_URL_SUCCESS:
      return {
        ...state,
        loading: false,
        memberCount: action.payload.memberCount,
        fileName: action.payload.fileName,
        processingPhase: 'COMPLETED'
      };

    case MemberLookupActionTypes.FETCH_DOWNLOAD_URL_FAILURE:
      return { ...state, loading: false, error: action.payload };

    case MemberLookupActionTypes.RESTORE_LOOKUP_ID:
      return { ...state, lookupId: action.payload };

    case MemberLookupActionTypes.RESET:
      return {
        ...INITIAL_MEMBER_LOOKUP_STATE,
        livePrice: state.livePrice,
        livePriceLoading: false,
        livePriceError: null
      };

    default:
      return state;
  }
};
